// The player table, shared by the expanded server card and the Players Online
// tab. One component because the two screens answer the same question and had
// no business drifting apart — a player shown as in-game on one screen and idle
// on the other is the bug this whole area exists to surface, not to create.

import { useState } from 'react'
import DateTimeFormated from '~/components/DateTimeFormated'
import { ROLES, useAllowed } from '~/team_tools/components/Capabilities'
import { BanButton, BanConfirm } from './BanPlayerButton'
import { KickButton, KickConfirm } from './KickPlayerButton'
import { ServerPlayer } from './api'

export const ACTION_COLOUR = {
    kill: 'text-green-500',
    death: 'text-red-500',
    spawn: 'text-blue-400',
    session_end: 'text-white/50',
}

export const ago = (seconds: number | null | undefined) => {
    if (seconds === null || seconds === undefined) return '-'
    if (seconds < 60) return `${seconds}s ago`
    if (seconds < 3600) return `${Math.round(seconds / 60)}m ago`
    if (seconds < 172800) return `${Math.round(seconds / 3600)}h ago`
    return `${Math.round(seconds / 86400)}d ago`
}

const COLUMNS = ['Player', 'Last action', 'When', 'Joined', 'Last seen']

export const PlayerRows = ({
    players,
    serverId,
    highlight = '',
    onBanned,
}: {
    players: ServerPlayer[]
    // Which server these players are on. Kick is per-server -- the same account
    // could be on another one, and disconnecting them from the wrong box would
    // look like the button silently failing.
    serverId: number
    // Lowercased username fragment to pick out, when a search is running.
    highlight?: string
    onBanned?: () => void
}) => {
    // The ban endpoint is developer-only, so a marketing account would see a
    // button that always 403s. Hide rather than disable: there is nothing they
    // could do to earn it in the moment.
    // Developer or executive, or a capability granted in the admin -- the same
    // people the ban and kick endpoints admit. Separate because a grant can
    // carry one and not the other.
    const canBan = useAllowed(ROLES.developer, 'ban_players')
    const canKick = useAllowed(ROLES.developer, 'kick_players')
    const canAct = canBan || canKick
    // Which row is asking. The confirmation renders below the table, not in the
    // cell: inside a nowrap table it cannot wrap and drags the page sideways.
    const [confirming, setConfirming] = useState<ServerPlayer | null>(null)
    // Kick asks separately from Ban: they are different promises (end this
    // session vs block the next login) and one confirmation cannot word both.
    const [kicking, setKicking] = useState<ServerPlayer | null>(null)

    return (
    <div className='flex flex-col gap-2 min-w-0'>
    <div className='overflow-x-auto'>
        <table className='w-full text-xs whitespace-nowrap'>
            <thead className='text-white/40'>
                <tr className='border-b border-white border-opacity-10'>
                    {[...COLUMNS, ...(canAct ? [''] : [])].map((h, i) => (
                        <th key={h || i} className='text-left font-normal uppercase tracking-wide py-1 pr-4'>
                            {h}
                        </th>
                    ))}
                </tr>
            </thead>
            <tbody>
                {players.map((p) => {
                    const matched = !!highlight && p.username.toLowerCase().includes(highlight)
                    return (
                        <tr
                            key={p.user_id}
                            className={[
                                'border-b border-white border-opacity-5',
                                matched ? 'bg-[#C91FFF] bg-opacity-10' : '',
                            ].join(' ')}
                        >
                            <td className='py-1 pr-4'>
                                <a
                                    target='_blank'
                                    href={`/team-tools/accounts/users/${p.username}`}
                                    className='font-bold hover:text-[#C91FFF]'
                                >
                                    {p.username}
                                </a>
                                {p.session?.in_progress && (
                                    <span className='ml-1 text-green-500' title='session in progress'>
                                        ●
                                    </span>
                                )}
                            </td>
                            <td className={`py-1 pr-4 ${ACTION_COLOUR[p.last_action?.type] ?? 'opacity-40'}`}>
                                {p.last_action?.detail ?? 'no activity in window'}
                            </td>
                            <td className='py-1 pr-4 opacity-70'>
                                {p.last_action ? (
                                    <span title={p.last_action.at}>{ago(p.last_action.seconds_ago)}</span>
                                ) : (
                                    '-'
                                )}
                            </td>
                            <td className='py-1 pr-4 opacity-70'>
                                <DateTimeFormated date={p.joined_at} className='text-xs' />
                            </td>
                            <td className='py-1 pr-4 opacity-70'>
                                <span title={p.last_seen_at}>{ago(p.seconds_since_seen)}</span>
                            </td>
                            {canAct && (
                                <td className='py-1 pr-4'>
                                    <span className='flex flex-row items-center gap-1'>
                                        {canKick && (
                                            <KickButton
                                                username={p.username}
                                                onAsk={() => setKicking(p)}
                                            />
                                        )}
                                        {canBan && (
                                            <BanButton
                                                username={p.username}
                                                onAsk={() => setConfirming(p)}
                                            />
                                        )}
                                    </span>
                                </td>
                            )}
                        </tr>
                    )
                })}
            </tbody>
        </table>
    </div>

    {kicking && (
        <KickConfirm
            serverId={serverId}
            userId={kicking.user_id}
            username={kicking.username}
            onDone={onBanned}
            onCancel={() => setKicking(null)}
        />
    )}

    {confirming && (
        <BanConfirm
            userId={confirming.user_id}
            username={confirming.username}
            onDone={onBanned}
            onCancel={() => setConfirming(null)}
        />
    )}
    </div>
    )
}
